/**
 * MODULE: domain/pages/frontend/components/DatabaseTableBody.tsx
 * PURPOSE: Notion-style table for database pages — inline cells, + New page row, + Add property header
 * RECENT: 2026-05-24 [2.2.1186] — flat grid replaces RecordTable shell; field-type icons; hover delete
 * AGENT_SAFETY: 🟢 SAFE
 * FILE_SIZE: 171/200
 */

import { useMemo } from 'react';
import { HiChevronDown, HiChevronUp, HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi';
import type { DatabaseRow, FieldDefinition } from '../../contracts/pages.types';
import type { CellSaveValue } from '../utils/databaseFieldUtils';
import { isFieldSortable } from '../utils/databaseFieldUtils';
import { DatabaseFieldIcon } from '../utils/databaseFieldIcons';
import { rowsToTableData, sortTableData } from '../utils/databaseTableModel';
import { DatabaseFieldCell } from './DatabaseFieldCell';

type Props = {
  pageTitle: string;
  fields: FieldDefinition[];
  rows: DatabaseRow[];
  sortKey?: string;
  sortDir: 'asc' | 'desc';
  onSort: (key: string) => void;
  onCellSave: (rowId: string, fieldId: string, value: CellSaveValue) => void;
  onDeleteRow: (rowId: string) => void;
  onAddRow: () => void;
  onAddProperty?: () => void;
  addRowPending?: boolean;
  readOnly?: boolean;
};

export function DatabaseTableBody({
  pageTitle,
  fields,
  rows,
  sortKey,
  sortDir,
  onSort,
  onCellSave,
  onDeleteRow,
  onAddRow,
  onAddProperty,
  addRowPending,
  readOnly = false,
}: Props) {
  const tableData = useMemo(() => {
    const data = rowsToTableData(rows, fields);
    return sortTableData(data, fields, sortKey, sortDir);
  }, [fields, rows, sortKey, sortDir]);

  const colCount = fields.length + (readOnly ? 0 : 1);

  return (
    <div className="min-w-0 overflow-x-auto" data-testid="database-notion-table">
      <table className="w-full min-w-[480px] border-collapse text-sm">
        <caption className="sr-only">{pageTitle} database</caption>
        <thead>
          <tr className="border-b border-gray-200/80 dark:border-gray-700/80">
            {fields.map((field) => {
              const sortable = isFieldSortable(field);
              const active = sortKey === field.id;
              return (
                <th
                  key={field.id}
                  scope="col"
                  className="group h-9 min-w-[140px] max-w-[280px] px-2 text-left font-normal"
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(field.id)}
                      className="inline-flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                    >
                      <DatabaseFieldIcon field={field} />
                      <span className="truncate">{field.name}</span>
                      {active ? (
                        sortDir === 'asc' ? (
                          <HiChevronUp className="h-3 w-3 shrink-0" aria-hidden />
                        ) : (
                          <HiChevronDown className="h-3 w-3 shrink-0" aria-hidden />
                        )
                      ) : null}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-1 text-xs text-gray-500 dark:text-gray-400">
                      <DatabaseFieldIcon field={field} />
                      <span className="truncate">{field.name}</span>
                    </span>
                  )}
                </th>
              );
            })}
            {!readOnly ? (
              <th scope="col" className="h-9 min-w-[120px] px-2 text-left font-normal">
                <button
                  type="button"
                  onClick={onAddProperty}
                  className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                  data-testid="pages-database-add-property"
                >
                  <HiOutlinePlus className="h-3 w-3 shrink-0" aria-hidden />
                  Add property
                </button>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {tableData.map((row) => (
            <tr
              key={String(row.id)}
              className="group border-b border-gray-100 hover:bg-gray-50/80 dark:border-gray-800/80 dark:hover:bg-gray-900/50"
              data-testid={`pages-database-row-${row.id}`}
            >
              {fields.map((field) => (
                <td key={field.id} className="h-9 max-w-[280px] truncate px-2 align-middle text-gray-800 dark:text-gray-200">
                  <DatabaseFieldCell
                    field={field}
                    value={row[field.id]}
                    onSave={(value) => onCellSave(String(row.id), field.id, value)}
                    readOnly={readOnly}
                  />
                </td>
              ))}
              {!readOnly ? (
                <td className="h-9 px-2 align-middle">
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('Delete this row?')) onDeleteRow(String(row.id));
                    }}
                    className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                    aria-label="Delete row"
                    data-testid={`pages-database-delete-${row.id}`}
                  >
                    <HiOutlineTrash className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </td>
              ) : null}
            </tr>
          ))}
          {!readOnly ? (
            <tr className="border-b border-gray-100 dark:border-gray-800/80">
              <td colSpan={colCount} className="h-9 px-2">
                <button
                  type="button"
                  onClick={onAddRow}
                  disabled={addRowPending || fields.length === 0}
                  className="inline-flex items-center gap-1.5 rounded px-1 py-0.5 text-sm text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                  data-testid="pages-database-new-page-row"
                >
                  <HiOutlinePlus className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {addRowPending ? 'Adding…' : 'New page'}
                </button>
              </td>
            </tr>
          ) : null}
          {tableData.length === 0 && readOnly ? (
            <tr>
              <td
                colSpan={fields.length || 1}
                className="h-24 px-2 text-center text-sm text-gray-500 dark:text-gray-400"
              >
                This database has no rows yet.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
